/**
 * Intelligence Context
 *
 * Provides Intelligence-specific state and user management.
 * Works alongside the main AuthContext from the SiteOperations app.
 */

import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import type { Protocol, Conversation, IntelligenceUser } from '../types';

interface IntelligenceContextType {
  user: IntelligenceUser | null;
  loading: boolean;
  role: 'admin' | 'user' | null;
  isAdmin: boolean;

  // Protocols
  protocols: Protocol[];
  currentProtocol: Protocol | null;
  setCurrentProtocol: (protocol: Protocol | null) => void;
  loadProtocols: () => Promise<void>;

  // Conversations
  conversations: Conversation[];
  currentConversation: Conversation | null;
  setCurrentConversation: (conversation: Conversation | null) => void;
  loadConversations: () => Promise<void>;
  createConversation: (title?: string, protocolId?: string) => Promise<Conversation>;
}

const IntelligenceContext = createContext<IntelligenceContextType | undefined>(undefined);

export function IntelligenceProvider({ children }: { children: ReactNode }) {
  const { user: authUser, currentSite } = useAuth();
  const [user, setUser] = useState<IntelligenceUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<'admin' | 'user' | null>(null);

  // Protocols state
  const [protocols, setProtocols] = useState<Protocol[]>([]);
  const [currentProtocol, setCurrentProtocol] = useState<Protocol | null>(null);

  // Conversations state
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);

  const isAdmin = role === 'admin';

  // Track if initial data has been fetched to avoid refetching on token refresh
  const initialFetchDone = useRef(false);
  const lastAuthUserId = useRef<string | null>(null);
  const lastSiteId = useRef<string | null>(null);

  // Load user role for Intelligence tool - only when user or site actually changes
  useEffect(() => {
    const loadUserRole = async () => {
      if (!authUser || !currentSite) {
        setUser(null);
        setRole(null);
        setLoading(false);
        return;
      }

      // Skip if we already loaded for this user/site combination
      if (lastAuthUserId.current === authUser.id && lastSiteId.current === currentSite.id && initialFetchDone.current) {
        return;
      }

      lastAuthUserId.current = authUser.id;
      lastSiteId.current = currentSite.id;

      try {
        // Fetch Intelligence role from tool_user_roles table via API
        const roleResponse = await api.get(`/api/my-tool-role/site_intelligence?site_id=${currentSite.id}`);
        const intelligenceRole = roleResponse.data?.role as 'admin' | 'user' | null;

        setRole(intelligenceRole);
        setUser({
          id: authUser.id,
          email: authUser.email,
          full_name: authUser.full_name,
          avatar_url: authUser.avatar_url ?? null,
          site_id: currentSite.id,
          role: intelligenceRole,
        });
        initialFetchDone.current = true;
      } catch (error) {
        console.error('[IntelligenceContext] Error loading user role:', error);
        setRole(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    loadUserRole();
  }, [authUser, currentSite]);

  // Load protocols - super_admins get all protocols, others get site-specific
  const loadProtocols = useCallback(async () => {
    if (!currentSite) {
      setProtocols([]);
      return;
    }

    try {
      // Super admins can see all protocols across all sites
      const isSuperAdmin = authUser?.role === 'super_admin';
      const url = isSuperAdmin
        ? '/api/intelligence/protocols?all=true'
        : `/api/intelligence/protocols?site_id=${currentSite.id}`;

      const response = await api.get(url);
      setProtocols(response.data || []);
    } catch (error) {
      console.error('[IntelligenceContext] Error loading protocols:', error);
      setProtocols([]);
    }
  }, [currentSite, authUser?.role]);

  // Load conversations for current user
  const loadConversations = useCallback(async () => {
    if (!user) {
      setConversations([]);
      return;
    }

    try {
      const response = await api.get('/api/intelligence/conversations');
      setConversations(response.data || []);
    } catch (error) {
      console.error('[IntelligenceContext] Error loading conversations:', error);
      setConversations([]);
    }
  }, [user]);

  // Create a new conversation
  const createConversation = useCallback(async (title?: string, protocolId?: string): Promise<Conversation> => {
    const response = await api.post('/api/intelligence/conversations', {
      title: title || 'New Conversation',
      protocol_id: protocolId,
      site_id: currentSite?.id,
    });
    const newConversation = response.data;
    setConversations(prev => [newConversation, ...prev]);
    setCurrentConversation(newConversation);
    return newConversation;
  }, [currentSite]);

  // Track if protocols/conversations have been loaded
  const protocolsLoaded = useRef(false);
  const conversationsLoaded = useRef(false);

  // Load protocols when site changes - only once per site
  useEffect(() => {
    if (currentSite && !loading) {
      // Only load if site changed or never loaded
      if (!protocolsLoaded.current || lastSiteId.current !== currentSite.id) {
        protocolsLoaded.current = true;
        loadProtocols();
      }
    }
  }, [currentSite, loading, loadProtocols]);

  // Load conversations when user is loaded - only once per user
  useEffect(() => {
    if (user && !loading) {
      // Only load if user changed or never loaded
      if (!conversationsLoaded.current || lastAuthUserId.current !== user.id) {
        conversationsLoaded.current = true;
        loadConversations();
      }
    }
  }, [user, loading, loadConversations]);

  return (
    <IntelligenceContext.Provider
      value={{
        user,
        loading,
        role,
        isAdmin,
        protocols,
        currentProtocol,
        setCurrentProtocol,
        loadProtocols,
        conversations,
        currentConversation,
        setCurrentConversation,
        loadConversations,
        createConversation,
      }}
    >
      {children}
    </IntelligenceContext.Provider>
  );
}

export function useIntelligence() {
  const context = useContext(IntelligenceContext);
  if (context === undefined) {
    throw new Error('useIntelligence must be used within an IntelligenceProvider');
  }
  return context;
}
